import type { FieldDef, JsonValue } from './types';

/**
 * The value of a TABLE field (D24): the text of each filled cell, by row key and column key:
 * { "R1": { "A": "12", "B": "10" }, "R2": { "A": "8" } }. Same rules as the backend TableValues.
 */
export type TableValue = Record<string, Record<string, string>>;

function isObject(v: JsonValue | undefined): v is Record<string, JsonValue> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Only the field's rows and columns, and only texts in the cells (anything else is rejected as invalidType). */
export function isTableValue(f: FieldDef, v: JsonValue | undefined): v is Record<string, JsonValue> {
  if (!isObject(v)) return false;
  const rows = new Set((f.table?.rows ?? []).map((r) => r.key));
  const columns = new Set((f.table?.columns ?? []).map((c) => c.key));
  return Object.entries(v).every(([row, cells]) => rows.has(row) && isObject(cells) && Object.entries(cells).every(([column, text]) => columns.has(column) && typeof text === 'string'));
}

/** The filled cells (blank ones left out), in the order of the definition; null when no cell is filled. */
export function filledCells(f: FieldDef, v: Record<string, JsonValue>): TableValue | null {
  const out: TableValue = {};
  for (const row of f.table?.rows ?? []) {
    const cells = v[row.key];
    if (!isObject(cells)) continue;
    for (const column of f.table?.columns ?? []) {
      const text = cells[column.key];
      if (typeof text === 'string' && text.trim() !== '') (out[row.key] ??= {})[column.key] = text;
    }
  }
  return Object.keys(out).length ? out : null;
}

/** The cells in reading order (row by row). */
export function cells(f: FieldDef): { row: string; column: string }[] {
  return (f.table?.rows ?? []).flatMap((r) => (f.table?.columns ?? []).map((c) => ({ row: r.key, column: c.key })));
}
